import React, { useMemo, useState } from "react";
import {
  ChartColumn,
  Zap,
  Clock,
  Users,
  Award,
  Target,
  TrendingUp,
  Send,
  CircleCheckBig,
  RefreshCw,
  FileText,
} from "lucide-react";

export default function TimesheetManagementShowcase() {
  const [mode, setMode] = useState("daily"); // daily | weekly | monthly

  const TAB_META = {
    daily: {
      title: "Daily",
      subtitle: "Today's check-in and check-out times with automated tracking",
      info: "Showing today's attendance (Dec 15, 2025)",
    },
    weekly: {
      title: "Weekly",
      subtitle: "Accurate hour tracking for every team member across the week",
      info: "Showing current week (Dec 9–13, 2025)",
    },
    monthly: {
      title: "Monthly",
      subtitle: "Comprehensive monthly overview with weekly breakdowns",
      info: "Showing December 2025 summary",
    },
  };

  const dailyRows = useMemo(
    () => [
      {
        name: "John Smith",
        role: "Developer",
        in: "9:00 AM",
        out: "6:00 PM",
        break: "1h",
        total: "8h",
      },
      {
        name: "Sarah Johnson",
        role: "Designer",
        in: "8:30 AM",
        out: "5:00 PM",
        break: "30m",
        total: "8h",
      },
      {
        name: "Mike Davis",
        role: "Manager",
        in: "8:00 AM",
        out: "6:00 PM",
        break: "1h",
        total: "9h",
      },
      {
        name: "Emily Brown",
        role: "Developer",
        in: "9:30 AM",
        out: "6:30 PM",
        break: "1h",
        total: "8h",
      },
      {
        name: "David Wilson",
        role: "QA Engineer",
        in: "9:00 AM",
        out: "5:30 PM",
        break: "30m",
        total: "8h",
      },
    ],
    [],
  );

  const weeklyRows = useMemo(
    () => [
      {
        name: "John Smith",
        role: "Developer",
        mon: "8h",
        tue: "8h",
        wed: "7.5h",
        thu: "8h",
        fri: "8h",
        total: "39.5h",
      },
      {
        name: "Sarah Johnson",
        role: "Designer",
        mon: "8h",
        tue: "8h",
        wed: "8h",
        thu: "6h",
        fri: "8h",
        total: "38h",
      },
      {
        name: "Mike Davis",
        role: "Manager",
        mon: "9h",
        tue: "8h",
        wed: "8h",
        thu: "8h",
        fri: "7h",
        total: "40h",
      },
      {
        name: "Emily Brown",
        role: "Developer",
        mon: "8h",
        tue: "8h",
        wed: "8h",
        thu: "8h",
        fri: "8h",
        total: "40h",
      },
      {
        name: "David Wilson",
        role: "QA Engineer",
        mon: "8h",
        tue: "7.5h",
        wed: "8h",
        thu: "8h",
        fri: "8h",
        total: "39.5h",
      },
    ],
    [],
  );

  const monthlyRows = useMemo(
    () => [
      {
        name: "John Smith",
        role: "Developer",
        w1: "40h",
        w2: "39.5h",
        w3: "40h",
        w4: "38h",
        total: "157.5h",
      },
      {
        name: "Sarah Johnson",
        role: "Designer",
        w1: "38h",
        w2: "40h",
        w3: "37h",
        w4: "39h",
        total: "154h",
      },
      {
        name: "Mike Davis",
        role: "Manager",
        w1: "42h",
        w2: "40h",
        w3: "41h",
        w4: "40h",
        total: "163h",
      },
      {
        name: "Emily Brown",
        role: "Developer",
        w1: "40h",
        w2: "40h",
        w3: "40h",
        w4: "40h",
        total: "160h",
      },
      {
        name: "David Wilson",
        role: "QA Engineer",
        w1: "39.5h",
        w2: "40h",
        w3: "39h",
        w4: "40h",
        total: "158.5h",
      },
    ],
    [],
  );

  const rowsCount =
    mode === "daily"
      ? dailyRows.length
      : mode === "weekly"
        ? weeklyRows.length
        : monthlyRows.length;

  return (
    <section className="tsSection">
      {/* background */}
      {/* <div className="tsBgGradient" /> */}
      {/* <div className="tsBgBlob" /> */}

      <div className="tsWrap">
        {/* header */}
        <div className="tsHeader tsAnimFadeUp">
          <div className="tsBadge">
            <ChartColumn size={16} />
            <span className="tsBadge-text">Automated Tracking</span>
          </div>

          <h2 className="tsTitle">Timesheet Management</h2>
          <p className="tsSubtitle">{TAB_META[mode].subtitle}</p>
        </div>

        {/* tabs */}
        <div className="tsTabsRow tsAnimFadeUp">
          <div className="tsTabs">
            {Object.keys(TAB_META).map((k) => {
              const active = mode === k;
              return (
                <button
                  key={k}
                  className={`tsTab ${active ? "isActive" : ""}`}
                  onClick={() => setMode(k)}
                  type="button"
                >
                  <span className="tsTabLabel">{TAB_META[k].title}</span>
                  {active && <span className="tsTabGlow" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* info bar */}
        <div className="tsInfo tsAnimFadeUp">
          <div className="tsInfoLeft">
            <span className="tsDot" />
            <span className="tsInfoText">{TAB_META[mode].info}</span>
          </div>
          <span className="tsPill">{rowsCount} employees</span>
        </div>

        {/* table/card container */}
        <div className="tsCard tsAnimFadeUp">
          {/* DESKTOP TABLE */}
          <div className="tsTableWrap">
            {mode === "daily" && (
              <table className="tsTable">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Role</th>
                    <th className="center">Check In</th>
                    <th className="center">Check Out</th>
                    <th className="center">Break</th>
                    <th className="center">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyRows.map((r, i) => (
                    <tr
                      key={r.name}
                      style={{ animationDelay: `${i * 45}ms` }}
                      className="tsRowIn"
                    >
                      <td className="strong">{r.name}</td>
                      <td className="muted">{r.role}</td>
                      <td className="center">{r.in}</td>
                      <td className="center">{r.out}</td>
                      <td className="center">{r.break}</td>
                      <td className="center">
                        <span className="tsChip">{r.total}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {mode === "weekly" && (
              <table className="tsTable">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Role</th>
                    <th className="center">Mon</th>
                    <th className="center">Tue</th>
                    <th className="center">Wed</th>
                    <th className="center">Thu</th>
                    <th className="center">Fri</th>
                    <th className="center">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {weeklyRows.map((r, i) => (
                    <tr
                      key={r.name}
                      style={{ animationDelay: `${i * 45}ms` }}
                      className="tsRowIn"
                    >
                      <td className="strong">{r.name}</td>
                      <td className="muted">{r.role}</td>
                      <td className="center">{r.mon}</td>
                      <td className="center">{r.tue}</td>
                      <td className="center">{r.wed}</td>
                      <td className="center">{r.thu}</td>
                      <td className="center">{r.fri}</td>
                      <td className="center">
                        <span className="tsChip">{r.total}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {mode === "monthly" && (
              <table className="tsTable">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Role</th>
                    <th className="center">Week 1</th>
                    <th className="center">Week 2</th>
                    <th className="center">Week 3</th>
                    <th className="center">Week 4</th>
                    <th className="center">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyRows.map((r, i) => (
                    <tr
                      key={r.name}
                      style={{ animationDelay: `${i * 45}ms` }}
                      className="tsRowIn"
                    >
                      <td className="strong">{r.name}</td>
                      <td className="muted">{r.role}</td>
                      <td className="center">{r.w1}</td>
                      <td className="center">{r.w2}</td>
                      <td className="center">{r.w3}</td>
                      <td className="center">{r.w4}</td>
                      <td className="center">
                        <span className="tsChip">{r.total}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* MOBILE CARDS */}
          <div className="tsMobile">
            {mode === "daily" &&
              dailyRows.map((r, i) => (
                <div
                  className="tsMCard tsRowIn"
                  key={r.name}
                  style={{ animationDelay: `${i * 55}ms` }}
                >
                  <div className="tsMTop">
                    <div>
                      <div className="tsMName">{r.name}</div>
                      <div className="tsMRole">{r.role}</div>
                    </div>
                    <span className="tsChip">{r.total}</span>
                  </div>

                  <div className="tsMGrid">
                    <Mini label="Check In" value={r.in} />
                    <Mini label="Check Out" value={r.out} />
                    <Mini label="Break" value={r.break} full />
                  </div>
                </div>
              ))}

            {mode === "weekly" &&
              weeklyRows.map((r, i) => (
                <div
                  className="tsMCard tsRowIn"
                  key={r.name}
                  style={{ animationDelay: `${i * 55}ms` }}
                >
                  <div className="tsMTop">
                    <div>
                      <div className="tsMName">{r.name}</div>
                      <div className="tsMRole">{r.role}</div>
                    </div>
                    <span className="tsChip">{r.total}</span>
                  </div>

                  <div className="tsMGrid3">
                    <Mini label="Mon" value={r.mon} />
                    <Mini label="Tue" value={r.tue} />
                    <Mini label="Wed" value={r.wed} />
                    <Mini label="Thu" value={r.thu} />
                    <Mini label="Fri" value={r.fri} />
                  </div>
                </div>
              ))}

            {mode === "monthly" &&
              monthlyRows.map((r, i) => (
                <div
                  className="tsMCard tsRowIn"
                  key={r.name}
                  style={{ animationDelay: `${i * 55}ms` }}
                >
                  <div className="tsMTop">
                    <div>
                      <div className="tsMName">{r.name}</div>
                      <div className="tsMRole">{r.role}</div>
                    </div>
                    <span className="tsChip">{r.total}</span>
                  </div>

                  <div className="tsMGrid">
                    <Mini label="Week 1" value={r.w1} />
                    <Mini label="Week 2" value={r.w2} />
                    <Mini label="Week 3" value={r.w3} />
                    <Mini label="Week 4" value={r.w4} />
                  </div>
                </div>
              ))}
          </div>

          {/* shimmer callout */}
          <div className="tsCallout tsShimmer">
            <div className="tsCallLeft">
              <span className="tsIconCircle">
                <Zap size={18} />
              </span>
              <div>
                <div className="tsCallTitle">✨ Fully Automated Tracking</div>
                <div className="tsCallDesc">
                  Hours auto-populated from check-in/check-out system
                </div>
              </div>
            </div>

            <div className="tsLive">
              <span className="tsLiveDot" />
              <span>Live Sync</span>
            </div>
          </div>
        </div>

        {/* stats */}
        <div className="tsStats tsAnimFadeUp">
          <Stat
            title="Total Hours This Week"
            value="197.0h"
            icon={<Clock />}
            tone="cyan"
          />
          <Stat
            title="Average Per Employee"
            value="39.4h"
            icon={<Users />}
            tone="blue"
          />
          <Stat
            title="Overtime Hours"
            value="7.0h"
            icon={<Award />}
            tone="cyan"
          />
        </div>

        {/* insights */}
        <div className="tsInsights tsAnimFadeUp">
          <Insight
            icon={<Target />}
            title="Peak Performance Hours"
            desc="Automated tracking reveals your team is most productive between 10 AM - 12 PM"
            footer={
              <div className="tsBarRow">
                <div className="tsBar">
                  <div className="tsBarFill" style={{ width: "85%" }} />
                </div>
                <span className="tsBarText">85%</span>
              </div>
            }
            tone="cyan"
          />

          <Insight
            icon={<TrendingUp />}
            title="Efficiency Improvements"
            desc="Teams using automated tracking save 4.5 hours per week on manual logging"
            footer={
              <div className="tsDeltaRow">
                <span className="tsDelta">↑ 23%</span>
                <span className="tsDeltaHint">vs. manual entry</span>
              </div>
            }
            tone="blue"
          />
        </div>

        {/* workflow */}
        <div className="tsWorkflow tsAnimFadeUp">
          <div className="tsWFHead">
            <h3>🔄 Automated Workflow</h3>
            <p>See how check-in automatically populates your timesheets</p>
          </div>

          <div className="tsWFRow">
            <FlowStep
              icon={<CircleCheckBig />}
              title="Check In"
              desc="Employee starts their day at 09:00 AM"
            />
            <FlowArrow />
            <FlowStep
              icon={<RefreshCw />}
              title="Auto-Track"
              desc="System tracks hours in real-time"
            />
            <FlowArrow />
            <FlowStep
              icon={<FileText />}
              title="Auto-Fill"
              desc="Timesheet automatically updated with 8h"
            />
          </div>

          <div className="tsStatus">
            <div className="tsStatusLeft">
              <span className="tsStatusDot" />
              <div>
                <div className="tsStatusTitle">
                  Current Status: ⏳ Ready to Check In
                </div>
                <div className="tsStatusDesc">
                  Click "Check In" to see live automation
                </div>
              </div>
            </div>

            <div className="tsTimer">00:00:00</div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- small components ---------------- */

function Mini({ label, value, full }) {
  return (
    <div className={`tsMini ${full ? "full" : ""}`}>
      <div className="tsMiniLabel">{label}</div>
      <div className="tsMiniValue">{value}</div>
    </div>
  );
}

function Stat({ title, value, icon, tone }) {
  return (
    <div className={`tsStatCard tone-${tone}`}>
      <div className="tsStatLeft">
        <div className="tsStatTitle">{title}</div>
        <div className="tsStatValue">{value}</div>
      </div>
      <div className="tsStatIcon">{React.cloneElement(icon, { size: 22 })}</div>
    </div>
  );
}

function Insight({ icon, title, desc, footer, tone }) {
  return (
    <div className={`tsInsight tone-${tone}`}>
      <div className="tsInsightIcon">
        {React.cloneElement(icon, { size: 22 })}
      </div>
      <div className="tsInsightBody">
        <h4>{title}</h4>
        <p>{desc}</p>
        {footer}
      </div>
    </div>
  );
}

function FlowStep({ icon, title, desc }) {
  return (
    <div className="tsStep">
      <div className="tsStepIcon">{React.cloneElement(icon, { size: 26 })}</div>
      <h5>{title}</h5>
      <p>{desc}</p>
    </div>
  );
}

function FlowArrow() {
  return (
    <div className="tsArrow">
      <Send size={22} />
    </div>
  );
}
